import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useState } from 'react';
import { CompareProvider } from './context/CompareContext';
import FloatingCompareButton from './components/FloatingCompareButton';
import CompareModal from './components/CompareModal';
import { useAuthStore } from './store/authStore';
import Web3Provider from './components/Web3Provider';

// Layout
import Layout from './components/layout/Layout';

// Pages
import HomePage from './pages/HomePage';
import PricingPage from './pages/PricingPage';
import StakingPage from './pages/StakingPage';
import ProtocolPage from './pages/ProtocolPage';
import LeaderboardPage from './pages/LeaderboardPage';
import ChangelogPage from './pages/ChangelogPage';
import FAQPage from './pages/FAQPage';
import EthereumStakingGuide from './pages/blog/EthereumStakingGuide';
import LiquidStakingGuide from './pages/blog/LiquidStakingGuide';
import StakingTaxGuide from './pages/blog/StakingTaxGuide';
import DashboardPage from './pages/DashboardPage';
import PortfolioPage from './pages/PortfolioPage';
import EducationPage from './pages/EducationPage';
import CoursePage from './pages/CoursePage';
import LessonPage from './pages/LessonPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import NotFoundPage from './pages/NotFoundPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import AboutPage from './pages/AboutPage';
import CalculatorLandingPage from './pages/CalculatorLandingPage';
import BlogPage from './pages/BlogPage';
import BestCryptoStakingRewards2026 from './pages/blog/BestCryptoStakingRewards2026';
import AtomStakingGuide from './pages/blog/AtomStakingGuide';
import TangemWalletGuide from './pages/blog/TangemWalletGuide';
import CoinStakingPage from './pages/CoinStakingPage';
import StorePage from './pages/StorePage';
import IntelligencePage from './pages/IntelligencePage';
import StoreSuccessPage from './pages/StoreSuccessPage';
import MyPurchasesPage from './pages/MyPurchasesPage';
import ReferralPage from './pages/ReferralPage';
import RiskAssessmentPage from './pages/RiskAssessmentPage';
import WelcomeModal from './components/WelcomeModal';

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuthStore();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

function App() {
  const [compareOpen, setCompareOpen] = useState(false);
  const [showWelcome, setShowWelcome] = React.useState(false);

  React.useEffect(() => {
    const auth = useAuthStore.getState();
    if (auth.isAuthenticated && auth.user && auth.token) {
      const API_BASE = (import.meta.env.VITE_API_URL || 'http://64.23.240.10/api/v1').replace('/v1', '');
      fetch(API_BASE + '/auth/check-welcome', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + auth.token }
      })
        .then(r => r.json())
        .then(d => { if (d.show_welcome) setShowWelcome(true); })
        .catch(() => {});
    }
  }, []);

  return (
    <CompareProvider maxCompare={5}>
      <WelcomeModal isOpen={showWelcome} onClose={() => setShowWelcome(false)} />
      <Web3Provider>
        <FloatingCompareButton onClick={() => setCompareOpen(true)} />
        <CompareModal isOpen={compareOpen} onClose={() => setCompareOpen(false)} />
        <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="staking" element={<StakingPage />} />
          <Route path="intelligence" element={<IntelligencePage />} />
          <Route path="risk" element={<RiskAssessmentPage />} />
          <Route path="staking/:coin" element={<CoinStakingPage />} />
          <Route path="protocol/:slug" element={<ProtocolPage />} />
          <Route path="leaderboard" element={<LeaderboardPage />} />
          <Route path="changelog" element={<ChangelogPage />} />
          <Route path="faq" element={<FAQPage />} />
          <Route path="calculator" element={<CalculatorLandingPage />} />
          <Route path="education" element={<EducationPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/best-crypto-staking-rewards-2026" element={<BestCryptoStakingRewards2026 />} />
          <Route path="blog/atom-staking-guide" element={<AtomStakingGuide />} />
          <Route path="blog/ethereum-staking-guide-2026" element={<EthereumStakingGuide />} />
          <Route path="blog/liquid-staking-guide-2026" element={<LiquidStakingGuide />} />
          <Route path="blog/crypto-staking-tax-guide-2026" element={<StakingTaxGuide />} />
          <Route path="blog/tangem-wallet-guide" element={<TangemWalletGuide />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="payment/success" element={<PaymentSuccessPage />} />
          <Route path="verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="store" element={<StorePage />} />
          <Route path="store/success" element={<StoreSuccessPage />} />
          <Route path="store/my-purchases" element={<ProtectedRoute><MyPurchasesPage /></ProtectedRoute>} />
          <Route path="dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="portfolio" element={<ProtectedRoute><PortfolioPage /></ProtectedRoute>} />
          <Route path="education/courses/:courseId" element={<ProtectedRoute><CoursePage /></ProtectedRoute>} />
          <Route path="education/lessons/:lessonId" element={<ProtectedRoute><LessonPage /></ProtectedRoute>} />
          <Route path="profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="referral" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      </Web3Provider>
    </CompareProvider>
  );
}

export default App;
